import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Plus,
  Loader2,
  Building2,
  MapPin,
  ArrowRight,
  Kanban,
  Users,
  Sparkles,
  CalendarPlus,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi, clearAdminToken } from "@/lib/adminApi";
import { LOGO_URL } from "@/lib/content";

export default function AdminVenues() {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedingEvents, setSeedingEvents] = useState(false);
  const [form, setForm] = useState({
    name: "",
    line1: "",
    city: "",
    postcode: "",
  });

  const seedDemo = async () => {
    if (
      !window.confirm(
        "Create or refresh the demo portal account?\n\nThis will make the standard demo credentials work on this environment:\n\n  Email: portal.test@example.com\n  Password: PortalTest123!\n\nSafe to run multiple times."
      )
    ) {
      return;
    }
    setSeeding(true);
    try {
      const { data } = await adminApi.post("/admin/seed-demo-portal", {});
      toast.success(
        data.created
          ? "Demo account created — you can now log in."
          : "Demo account already existed — password refreshed."
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't seed the demo account.");
    } finally {
      setSeeding(false);
    }
  };

  const seedEvents = async () => {
    if (
      !window.confirm(
        "Seed 3 demo events on the Test Venue?\n\n  • Saturday Social (recurring — every Saturday)\n  • Friday Mix (recurring — every Friday)\n  • Black Tie Anniversary (one-off, ~3 weeks out)\n\nSafe to run multiple times — existing events will be refreshed, not duplicated."
      )
    ) {
      return;
    }
    setSeedingEvents(true);
    try {
      const { data } = await adminApi.post("/admin/seed-demo-events", {});
      const c = data.events_created;
      const u = data.events_updated;
      toast.success(
        `Demo events ready — ${c} created, ${u} refreshed.`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't seed the demo events.");
    } finally {
      setSeedingEvents(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/venues");
      setVenues(data);
    } catch {
      // interceptor handles 401
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  };

  const createVenue = async (e) => {
    e.preventDefault();
    if (creating) return;
    const { name, line1, city, postcode } = form;
    if (!name || !line1 || !city || !postcode) {
      toast.error("Name and address fields are required.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await adminApi.post("/admin/venues", {
        name: name.trim(),
        address: { line1: line1.trim(), city: city.trim(), postcode: postcode.trim() },
      });
      toast.success("Venue created.");
      setForm({ name: "", line1: "", city: "", postcode: "" });
      navigate(`/admin/venues/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to create venue.";
      toast.error(typeof detail === "string" ? detail : "Create failed.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      data-testid="admin-venues-page"
      className="min-h-screen bg-black text-[#F5F5F0]"
    >
      {/* HEADER */}
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-8 flex items-center justify-between h-20">
          <div className="flex items-center gap-6">
            <Link to="/admin">
              <img src={LOGO_URL} alt="Swingle" className="h-12 w-auto object-contain" />
            </Link>
            <nav className="flex items-center gap-4">
              <Link
                to="/admin"
                data-testid="admin-nav-pipeline"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
              >
                <Kanban size={14} /> Pipeline
              </Link>
              <Link
                to="/admin/venues"
                data-testid="admin-nav-venues"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]"
              >
                <Building2 size={14} /> Venues
              </Link>
              <Link
                to="/admin/applications"
                data-testid="admin-nav-applications"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
              >
                <Building2 size={14} /> Applications
              </Link>
              <Link
                to="/admin/contacts"
                data-testid="admin-nav-contacts"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
              >
                <Users size={14} /> Contacts
              </Link>
            </nav>
          </div>
          <button
            type="button"
            data-testid="venues-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#D9A6F5] px-3 py-2 text-xs uppercase tracking-[0.2em] transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-8 py-10">
        <div className="mb-10 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Client Venues</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
              {loading ? "Loading…" : `${venues.length} venue${venues.length === 1 ? "" : "s"}`}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="admin-seed-demo-btn"
              onClick={seedDemo}
              disabled={seeding}
              className="inline-flex items-center gap-2 border border-[#D9A6F5] text-[#D9A6F5] hover:bg-[#D9A6F5] hover:text-black disabled:opacity-40 px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              {seeding ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Seed demo portal account
            </button>
            <button
              type="button"
              data-testid="admin-seed-events-btn"
              onClick={seedEvents}
              disabled={seedingEvents}
              className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] disabled:opacity-40 px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              {seedingEvents ? <Loader2 size={12} className="animate-spin" /> : <CalendarPlus size={12} />}
              Seed demo events
            </button>
          </div>
        </div>

        {/* Create form */}
        <div className="brackets bg-[#0A0A0A] border border-white/10 p-6 md:p-8 mb-12">
          <div className="text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-5">Add new venue</div>
          <form onSubmit={createVenue} className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="venues-create-form" noValidate>
            <Input
              data-testid="venues-create-name"
              placeholder="Venue name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
              required
            />
            <Input
              data-testid="venues-create-line1"
              placeholder="Address line 1 *"
              value={form.line1}
              onChange={(e) => setForm({ ...form, line1: e.target.value })}
              className={inputCls}
              required
            />
            <Input
              data-testid="venues-create-city"
              placeholder="City *"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className={inputCls}
              required
            />
            <Input
              data-testid="venues-create-postcode"
              placeholder="Postcode *"
              value={form.postcode}
              onChange={(e) => setForm({ ...form, postcode: e.target.value })}
              className={inputCls}
              required
            />
            <button
              type="submit"
              disabled={creating}
              data-testid="venues-create-submit"
              className="md:col-span-4 inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-60 px-6 py-3.5 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create Venue
            </button>
          </form>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
          </div>
        ) : venues.length === 0 ? (
          <div className="text-center py-20 text-[#A3A3A3]">
            No venues yet. Add your first one above.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {venues.map((v) => (
              <Link
                key={v.id}
                to={`/admin/venues/${v.id}`}
                data-testid={`venue-card-${v.id}`}
                className="group bg-[#0A0A0A] border border-white/10 hover:border-[#D9A6F5]/50 p-6 transition-colors"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="h-12 w-12 shrink-0 bg-black border border-white/10 flex items-center justify-center overflow-hidden">
                    {v.logo_data_uri ? (
                      <img src={v.logo_data_uri} alt={v.name} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <Building2 size={18} className="text-[#D9A6F5]" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-lg font-bold text-[#F5F5F0] truncate">{v.name}</div>
                    <div className="text-xs text-[#A3A3A3] uppercase tracking-[0.2em] font-mono truncate">
                      /v/{v.slug}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-[#A3A3A3] flex items-start gap-2 mb-4">
                  <MapPin size={12} className="mt-1 shrink-0 text-[#D9A6F5]" />
                  <span className="truncate">
                    {v.address?.line1}, {v.address?.city} · {v.address?.postcode}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs uppercase tracking-[0.22em]">
                  <span className="text-[#A3A3A3]">Manage</span>
                  <ArrowRight
                    size={14}
                    className="text-[#A3A3A3] group-hover:text-[#D9A6F5] group-hover:translate-x-1 transition-all"
                  />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "bg-black border-white/15 rounded-none h-12 text-[#F5F5F0] placeholder:text-[#A3A3A3]/60 focus-visible:ring-[#D9A6F5] focus-visible:ring-offset-0 w-full px-3";
